import { QuestionCircleOutlined } from '@ant-design/icons'
import GenericLoading from '@cannect/components/GenericLoading'
import { Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import * as S from './styles'

interface Association {
  id: number
  name: string
}

interface PeopleAssociation {
  id: number
  association_id: number
  status: 'active' | 'inactive' | 'pending' | 'expired'
  start_date: string | null
  end_date: string | null
  type: 'patient' | 'responsible' | 'company'
  association: Association
}

interface Person {
  id: number
  name: string
  email: string
  cpf: string
  phone: string
  birthday: string
  peopleAssociations: PeopleAssociation[]
}

interface User {
  id: number
  name: string
  email: string
  person: Person
}

interface MeResponse {
  success: boolean
  user: User
  needToPay?: boolean
}

const STATUS_LABELS = {
  active: 'ATIVADO',
  inactive: 'INATIVO',
  pending: 'PENDENTE',
  expired: 'EXPIRADO'
}

const TYPE_LABELS = {
  patient: 'Paciente',
  responsible: 'Responsável',
  company: 'Empresa'
}

const PRODUCTS_PATH = '/AreaAssociados/MinhaConta?pagina=carrinho&subpagina=produtos'
const DOCUMENTS_PATH = '/AreaAssociados/MinhaConta?pagina=perfil-documentos'

export default function InfoPage() {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userData, setUserData] = useState<User | null>(null)

  const fetchUserData = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data } = await api.get<MeResponse>('/me?association_id=1')

      if (data.success && data.user) {
        setUserData(data.user)
      } else {
        setError('Não foi possível carregar os dados do usuário')
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Erro ao carregar informações do usuário'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Não informado'

    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  useEffect(() => {
    fetchUserData()
  }, [])

  if (loading) {
    return (
      <S.LoadingContainer>
        <GenericLoading loading={loading} />
      </S.LoadingContainer>
    )
  }

  if (error || !userData) {
    return (
      <S.Container>
        <S.ErrorContainer>
          <p>{error || 'Não foi possível carregar as informações'}</p>
        </S.ErrorContainer>
      </S.Container>
    )
  }

  const association = userData.person?.peopleAssociations?.[0]

  if (!association) {
    return (
      <S.Container>
        <S.ErrorContainer>
          <p>Nenhuma associação encontrada para este usuário</p>
        </S.ErrorContainer>
      </S.Container>
    )
  }

  const memberSinceValue = formatDate(association.start_date)
  const showMemberSinceTooltip = !association.start_date || memberSinceValue === 'Não informado'
  const isPending = association.status === 'pending'
  const isActive = association.status === 'active'

  return (
    <S.Container>
      <S.Card>
        <S.Header>
          <S.UserName>{userData.person.name}</S.UserName>
        </S.Header>

        <S.InfoGrid>
          <S.InfoItem>
            <S.InfoLabel>Associado Nº:</S.InfoLabel>
            <S.InfoValue>{association.id}</S.InfoValue>
          </S.InfoItem>

          <S.InfoItem>
            <S.InfoLabel>Membro desde:</S.InfoLabel>
            <S.InfoValue>
              {memberSinceValue}
              {showMemberSinceTooltip && (
                <Tooltip
                  title="A data inicial será colocada a partir do momento da confirmação de pagamento de um pedido com nossos produtos"
                  placement="top"
                  overlayStyle={{ maxWidth: 300 }}>
                  <QuestionCircleOutlined
                    style={{
                      marginLeft: 8,
                      color: '#02b0bb',
                      cursor: 'pointer',
                      fontSize: 16
                    }}
                  />
                </Tooltip>
              )}
            </S.InfoValue>
          </S.InfoItem>

          <S.InfoItem>
            <S.InfoLabel>Tipo de associado:</S.InfoLabel>
            <S.InfoValue>{TYPE_LABELS[association.type]}</S.InfoValue>
          </S.InfoItem>

          <S.InfoItem>
            <S.InfoLabel>Situação atual:</S.InfoLabel>
            <S.InfoValue>{STATUS_LABELS[association.status]}</S.InfoValue>
          </S.InfoItem>
        </S.InfoGrid>

        {(isPending || isActive) && (
          <S.NextStepCard>
            {isPending && (
              <>
                <S.NextStepTitle>E agora?</S.NextStepTitle>
                <S.NextStepText>Faça um pedido com nossos produtos para concluir seu cadastro.</S.NextStepText>
              </>
            )}
            <S.OrderButton type="button" onClick={() => history.push(PRODUCTS_PATH)}>
              Faça seu pedido agora
            </S.OrderButton>
            {/* <S.TermButton type="button" onClick={() => history.push(DOCUMENTS_PATH)}>
              Ver termo de ajuizamento
            </S.TermButton> */}
            {isPending && (
              <S.NextStepHint>
                O termo fica disponível para baixar depois do pedido, na página de documentos.
              </S.NextStepHint>
            )}
          </S.NextStepCard>
        )}

        {/* <S.WarningCard>
          <S.WarningContent>
            <h3>Taxa isenta</h3>
            <p>Informamos que a taxa de associação será isenta neste momento.</p>
          </S.WarningContent>
        </S.WarningCard> */}
      </S.Card>
    </S.Container>
  )
}
